import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import IMask from "imask";

export const imask_definitions = {
  "A": {
    mask: /[A-Z]/,
    prepareChar: (char: string) => char.toUpperCase(),
  },
  "@": {
    mask: /[a-zA-Z]/,
  },
};

dayjs.extend(utc);

export function getNonEmptyRows(rows: string[][]) {
  const completelyFilledRows = rows.filter((row) =>
    row.some((item) => item.trim() !== "")
  );
  return completelyFilledRows;
}

/**
 * Returns a valid currency code based on the input value.
 * @param value - The currency code string to validate.
 * @returns The input currency code if it is valid, or "USD" if it is not valid.
 */
export const getCurrency = (value: string | undefined): string => {
  if (typeof value !== "string") return "USD";
  value = value.toUpperCase();
  const currencyCodes = [
    "AED",
    "AFN",
    "ALL",
    "AMD",
    "ANG",
    "AOA",
    "ARS",
    "AUD",
    "AWG",
    "AZN",
    "BAM",
    "BBD",
    "BDT",
    "BGN",
    "BHD",
    "BIF",
    "BMD",
    "BND",
    "BOB",
    "BOV",
    "BRL",
    "BSD",
    "BTN",
    "BWP",
    "BYR",
    "BZD",
    "CAD",
    "CDF",
    "CHE",
    "CHF",
    "CHW",
    "CLF",
    "CLP",
    "CNY",
    "COP",
    "COU",
    "CRC",
    "CUC",
    "CUP",
    "CVE",
    "CZK",
    "DJF",
    "DKK",
    "DOP",
    "DZD",
    "EGP",
    "ERN",
    "ETB",
    "EUR",
    "FJD",
    "FKP",
    "GBP",
    "GEL",
    "GHS",
    "GIP",
    "GMD",
    "GNF",
    "GTQ",
    "GYD",
    "HKD",
    "HNL",
    "HRK",
    "HTG",
    "HUF",
    "IDR",
    "ILS",
    "INR",
    "IQD",
    "IRR",
    "ISK",
    "JMD",
    "JOD",
    "JPY",
    "KES",
    "KGS",
    "KHR",
    "KMF",
    "KPW",
    "KRW",
    "KWD",
    "KYD",
    "KZT",
    "LAK",
    "LBP",
    "LKR",
    "LRD",
    "LSL",
    "LTL",
    "LVL",
    "LYD",
    "MAD",
    "MDL",
    "MGA",
    "MKD",
    "MMK",
    "MNT",
    "MOP",
    "MRO",
    "MUR",
    "MVR",
    "MWK",
    "MXN",
    "MXV",
    "MYR",
    "MZN",
    "NAD",
    "NGN",
    "NIO",
    "NOK",
    "NPR",
    "NZD",
    "OMR",
    "PAB",
    "PEN",
    "PGK",
    "PHP",
    "PKR",
    "PLN",
    "PYG",
    "QAR",
    "RON",
    "RSD",
    "RUB",
    "RWF",
    "SAR",
    "SBD",
    "SCR",
    "SDG",
    "SEK",
    "SGD",
    "SHP",
    "SLL",
    "SOS",
    "SRD",
    "SSP",
    "STD",
    "SYP",
    "SZL",
    "THB",
    "TJS",
    "TMT",
    "TND",
    "TOP",
    "TRY",
    "TTD",
    "TWD",
    "TZS",
    "UAH",
    "UGX",
    "USD",
    "USN",
    "USS",
    "UYI",
    "UYU",
    "UZS",
    "VEF",
    "VND",
    "VUV",
    "WST",
    "XAF",
    "XAG",
    "XAU",
    "XBA",
    "XBB",
    "XBC",
    "XBD",
    "XCD",
    "XDR",
    "XFU",
    "XOF",
    "XPD",
    "XPF",
    "XPT",
    "XTS",
    "XXX",
    "YER",
    "ZAR",
    "ZMW",
  ];
  if (currencyCodes.includes(value)) return value;
  else return "USD";
};

export function isISODate(dateString: string) {
  // Parsing the dateString using dayjs UTC mode
  const date = dayjs(dateString).utc();

  // If the date is invalid, return false
  if (!date.isValid()) return false;

  // Check if the formatted date is the same as the input dateString
  return date.toISOString() === dateString;
}

/**
 * Validates if a string is a valid date in the format YYYY-MM-DD.
 * @param date - The date string to validate.
 * @returns True if the date is valid, false otherwise.
 */
export const isValidDate = (date: string): boolean => {
  if (typeof date !== "string") {
    return false;
  }
  const regExp = /^\d{4}-\d{2}-\d{2}$/;
  if (!date.match(regExp)) {
    return false; // Invalid format
  }
  const d = new Date(date);
  if (isNaN(d.getTime())) {
    return false; // Invalid date
  }
  return d.toISOString().slice(0, 10) === date;
};

/**
 * Validates if a string is a valid time in the format 'HH:mm'.
 * @param time - The time string to validate.
 * @returns True if the time is valid, false otherwise.
 */
export const isValidTime = (time: string): boolean => {
  const format = "HH:mm";
  return dayjs(time, format, true).isValid();
};

/**
 * Validates if a string is a valid email address in RFC 5322 format.
 * @param email - The email address string to validate.
 * @returns True if the email address is valid, false otherwise.
 */
export const isValidEmail = (email: string | object | undefined): boolean => {
  if (typeof email !== "string") return false;
  const emailRegexp: RegExp = new RegExp(
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  );
  return emailRegexp.test(email);
};

/**
 * Validates if a string is a valid URL.
 * @param url - The URL string to validate.
 * @returns True if the URL is valid, false otherwise.
 */
export const isValidUrl = (urlString: string | object) => {
  if (typeof urlString !== "string") return false;
  try {
    return Boolean(new URL(urlString));
  } catch (e) {
    return false;
  }
};

/**
 * Validates a string against a given regular expression.
 * @param {string} str - The string to validate.
 * @param {string} regexStr - The regular expression as a string to use for validation.
 * @returns {boolean} - `true` if the string matches the regular expression, `false` otherwise.
 */
export function isValidAgainstRegexp(str: string, regexpStr: string): boolean {
  const regexp = new RegExp(regexpStr, "gu");
  return regexp.test(str);
}

export function isValidAgainstPattern(str: string, pattern: string): boolean {
  const masked = IMask.createMask({
    mask: pattern,
  });
  masked.resolve(str);
  return Boolean(masked.isComplete);
}

/**
 * Checks if a given string is a valid number.
 * @param {string} str - The string to check.
 * @param {boolean} allowSeparators - If true, ensures the string contains numbers or separators. If false, ensures the string is all digits.
 * @returns {boolean} - `true` if the string is a valid number, `false` otherwise.
 */
export function isValidNumber(
  str: string,
  allowSeparators: boolean = false,
): boolean {
  if (allowSeparators) {
    return /^[0-9,\-\.]+$/.test(str);
  } else {
    return /^\d+$/.test(str);
  }
}

/**
 * Checks whether a string contains only alphanumeric characters, spaces, and non-English alphabets.
 * @param {string} str - The string to check.
 * @returns {boolean} - Returns true if the string contains only alphanumeric characters, spaces, and non-English alphabets; otherwise, returns false.
 */
export function isValidAlphanumeric(str: string): boolean {
  const pattern: RegExp = /^[\p{Letter}\p{Mark}\s\p{Nd}]+$/u;
  return pattern.test(str);
}

export function isValidAlpha(str: string): boolean {
  const pattern: RegExp = /^[\p{Letter}\p{Mark}\s]+$/u;
  return pattern.test(str);
}

/**
 * Checks if a field is an empty string, null, undefined, or an empty array.
 *
 * @param {string | null | undefined | unknown[] | object} value - The value to check.
 * @returns {boolean} - `true` if the value is empty, `false` otherwise.
 */
export const isFieldEmpty = (
  value: string | null | undefined | unknown[] | object,
): boolean => {
  if (typeof value === "string") {
    return value.trim() === "";
  } else if (Array.isArray(value)) {
    return value.length === 0;
  } else if (
    typeof value !== "undefined" && value &&
    typeof (value as any).rows !== "undefined"
  ) {
    const filled_rows = getNonEmptyRows((value as any).rows as string[][]);
    return filled_rows.length === 0;
  } else {
    return value === null || value === undefined;
  }
};

/**
 * Checks if a field is an empty string, null, undefined, or an empty array.
 *
 * @param {string | null | undefined | unknown[] | object} value - The value to check.
 * @returns {boolean} - `true` if the value is empty, `false` otherwise.
 */
export const hasFieldNoValue = (
  value: string | null | undefined | unknown[] | object,
): boolean => {
  if (typeof value === "string") {
    return value === "";
  } else if (Array.isArray(value)) {
    return value.length === 0;
  } else if (
    typeof value !== "undefined" && typeof (value as any).rows !== "undefined"
  ) {
    const nonEmptyRows = ((value as any).rows as string[][]).filter((row) =>
      row.some((cell) => cell !== "")
    );
    return nonEmptyRows.length === 0;
  } else {
    return value === null || value === undefined;
  }
};
